// Componente de métricas de estado de pedidos
// Order status metrics component

import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faTruck, faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import { SectionBox } from '@shared/components/ui/SectionBox';
import { useOrdersKpis } from '../../hooks/useOrdersKpis';

interface OrderStatusMetricsProps {
  filters?: {
    shop_id?: number;
    cutoff_time?: string;
    date?: string;
  };
}

export const OrderStatusMetrics = ({ filters = {} }: OrderStatusMetricsProps) => {
  const { t } = useTranslation('homeModule');
  const { ordersKpis, isLoading } = useOrdersKpis(filters);

  const metrics = [
    {
      title: t('order_status.to_confirm.title'),
      count: ordersKpis.to_confirm.toString(),
      icon: faClock,
      className: 'bg-card-bg',
      iconBgClass: 'bg-card-border', // Por confirmar - gris por defecto
      iconTextClass: 'text-icons', // Color por defecto
    },
    {
      title: t('order_status.to_prepare.title'),
      count: ordersKpis.pending.toString(),
      icon: faClock,
      className: 'bg-card-bg',
      iconBgClass: 'bg-bg-warning', // Por preparar - naranja/amarillo
      iconTextClass: 'text-warning', // Color de advertencia
    },
    {
      title: t('order_status.in_preparation.title'),
      count: ordersKpis.preparing.toString(),
      icon: faTruck,
      className: 'bg-card-bg',
      iconBgClass: 'bg-bg-info', // En preparación - azul
      iconTextClass: 'text-info', // Color de información
    },
    {
      title: t('order_status.prepared.title'),
      count: ordersKpis.sent.toString(),
      icon: faCheckCircle,
      className: 'bg-card-bg',
      iconBgClass: 'bg-bg-success', // Preparados - verde
      iconTextClass: 'text-success', // Color de éxito
    },
  ];

  if (isLoading) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 !mt-4">
      {metrics.map((metric, index) => (
        <SectionBox key={index} className={metric.className} data-testid={`order-metric-${index}`}>
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-sm font-medium text-subtext mb-1">{metric.title}</h3>
              <p className="text-2xl font-bold text-title">{metric.count}</p>
            </div>
            <div className={`w-8 h-8 rounded-md ${metric.iconBgClass} flex items-center justify-center`}>
              <FontAwesomeIcon icon={metric.icon} className={`w-4 h-4 ${metric.iconTextClass}`} />
            </div>
          </div>
        </SectionBox>
      ))}
    </div>
  );
};
